import React, { useRef, useState, useEffect } from "react";
import { FaEllipsisVertical, FaCirclePlay, FaCirclePause, FaCakeCandles, FaEnvelope, FaCartShopping, FaRotateRight, FaUserClock } from "react-icons/fa6";
import { PrimaryButton } from "@/components/Buttons.jsx";
import { useNavigate } from "react-router-dom";
import { getCrmCampaigns } from "@/api/crm.js";
import InlineLoader from "@/components/InlineLoader.jsx";
import Tooltip from "@/components/Tooltip";

// Helper function to get campaign icon based on type
const getCampaignIcon = (type) => {
  switch (type) {
    case "welcome":
      return <FaEnvelope className="inline mr-2 text-indigo-500" />;
    case "cart_abandonment":
      return <FaCartShopping className="inline mr-2 text-pink-500" />;
    case "reengagement":
      return <FaUserClock className="inline mr-2 text-yellow-500" />;
    case "birthday":
      return <FaCakeCandles className="inline mr-2 text-green-500" />;
    default:
      return <FaEnvelope className="inline mr-2 text-slate-500" />;
  }
};

// Helper function to get campaign status display
const getCampaignStatus = (active) => {
  return active ? "Enabled" : "Disabled";
};

const statusColor = (status) => {
  if (status === "Enabled") return "bg-green-100 text-green-700";
  if (status === "Disabled") return "bg-yellow-100 text-yellow-700";
  return "bg-slate-100 text-slate-700";
};

const CampaignContextMenu = ({ show, setShow, onEdit, anchorRef, buttonRef }) => {
  const [position, setPosition] = useState({ top: 0, right: 0 });

  // Calculate position when menu opens
  useEffect(() => {
    if (show && buttonRef?.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const menuHeight = 120; // Approximate height of the menu
      
      // Check if menu would overflow bottom of viewport
      const wouldOverflowBottom = buttonRect.bottom + menuHeight > viewportHeight;
      
      setPosition({
        top: wouldOverflowBottom ? buttonRect.top - menuHeight : buttonRect.bottom,
        right: window.innerWidth - buttonRect.right
      });
    }
  }, [show, buttonRef]);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target) && 
          buttonRef?.current && !buttonRef.current.contains(event.target)) {
        setShow(false);
      }
    };
    if (show) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [show, setShow, anchorRef, buttonRef]);

  if (!show) return null;

  return (
    <div
      ref={anchorRef}
      className="fixed w-48 py-2 z-50 bg-white dark:bg-theme-surface-accent rounded-sm shadow-lg border border-slate-200 dark:border-slate-700"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`
      }}
    >
      <span
        onClick={() => { setShow(false); onEdit(); }}
        className="flex items-center justify-between px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-900 text-slate-800 dark:text-slate-100 cursor-pointer"
      >
        Edit
      </span>
    </div>
  );
};

const CrmCampaigns = () => {
  const [contextMenuIdx, setContextMenuIdx] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contextRefs = useRef([]);
  const navigate = useNavigate();

  // Fetch campaigns on component mount
  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCrmCampaigns();
      if (response.status === "success") {
        setCampaigns(response.campaigns || []);
      } else {
        setError("Failed to fetch campaigns");
      }
    } catch (err) {
      setError("Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  // Handlers for context menu actions
  const handleEdit = (campaign) => navigate(`/crm/campaigns/${campaign.id}`);

  // Loading state
  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full h-full mx-auto relative">
        <InlineLoader message="Loading campaigns..." minHeight="h-64" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full h-full mx-auto relative">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-4">{error}</div>
            <PrimaryButton onClick={fetchCampaigns}>Retry</PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full h-full mx-auto relative">
      {/* Page header */}
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        {/* Left: Title */}
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            CRM Campaigns
          </h1>
        </div>
      </div>
      
      {/* Campaigns Table */}
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="font-semibold text-slate-800 dark:text-slate-100">
            {campaigns.length} Campaign{campaigns.length !== 1 ? 's' : ''}
          </div>
          {/* Add filters/date pickers here if needed */}
        </div>
        
        {campaigns.length === 0 ? (
          <div className="px-6 py-8 text-center text-slate-500">
            No campaigns found. Create your first campaign to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-slate-50 dark:bg-slate-900/20">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold"><span className="flex items-center gap-2">Delay <Tooltip position="bottom" size="md">The amount of time before the email(s) will go out once triggered by the event parameter.</Tooltip></span></th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign, i) => {
                  const status = getCampaignStatus(campaign.active);
                  return (
                    <tr key={campaign.id} className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/10 relative">
                      <td className="px-4 py-3 whitespace-nowrap font-medium flex items-center gap-2">
                        {getCampaignIcon(campaign.type)}
                        {campaign.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="capitalize">{campaign.type.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${statusColor(status)}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {campaign.type !== "welcome" && campaign.delay_value && campaign.delay_unit ? (
                          <span>{campaign.delay_value} {campaign.delay_unit}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {campaign.created_at ? (
                          <span>{new Date(campaign.created_at).toLocaleDateString()}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right relative">
                        <button
                          className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setContextMenuIdx(contextMenuIdx === i ? null : i);
                          }}
                          ref={el => contextRefs.current[i] = el}
                        >
                          <FaEllipsisVertical />
                        </button>
                        <CampaignContextMenu
                          show={contextMenuIdx === i}
                          setShow={(v) => setContextMenuIdx(v ? i : null)}
                          onEdit={() => handleEdit(campaign)}
                          anchorRef={{ current: contextRefs.current[i] }}
                          buttonRef={{ current: contextRefs.current[i] }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrmCampaigns; 